/**
 * Every AI Clinical Copilot endpoint the portal calls, and nothing else.
 *
 * Mirrors `src/services/video-call/video-call.service.ts`: a flat map of the
 * service's routes, so the set of endpoints this app depends on is one file
 * long and reviewable at a glance.
 */

import { aiAssistClient } from "@/lib/api/aiAssistClient";
import type {
  AnalyseResponse,
  AuditEvent,
  ClinicalAction,
  ClinicalBucket,
  ConsultationSummary,
  CopilotContext,
  IssueResponse,
  Prescription,
  SessionResponse,
  TranscriptResponse,
  TranscriptSegment,
} from "@/types/copilot";

export interface OutgoingSegment {
  text: string;
  speaker?: "doctor" | "patient" | "unknown";
  state?: "interim" | "final";
  /** The recognizer's own id — what lets an interim be finalised in place. */
  externalId?: string;
  startMs?: number;
  endMs?: number;
  confidence?: number;
}

export const copilotService = {
  /* ── Session (backend §1) ────────────────────────────────────────────── */

  /** Opens or resumes the AI session for a booking. Resume-first. */
  openSession: (bookingId: string) =>
    aiAssistClient.post<SessionResponse>(`/bookings/${bookingId}/session`),

  /** Resume on mount. 404 = nothing to resume. */
  getSessionForBooking: (bookingId: string, signal?: AbortSignal) =>
    aiAssistClient.get<SessionResponse>(`/bookings/${bookingId}/session`, signal),

  getSession: (sessionId: string, signal?: AbortSignal) =>
    aiAssistClient.get<SessionResponse>(`/sessions/${sessionId}`, signal),

  setState: (sessionId: string, patch: { transcriptState?: string; status?: string }) =>
    aiAssistClient.patch<SessionResponse>(`/sessions/${sessionId}/state`, patch),

  /* ── Transcription (backend §2) ──────────────────────────────────────── */

  appendTranscript: (sessionId: string, segments: OutgoingSegment[]) =>
    aiAssistClient.post<{ stored: number; segments: TranscriptSegment[] }>(
      `/sessions/${sessionId}/transcript`,
      { segments },
    ),

  getTranscript: (sessionId: string, signal?: AbortSignal) =>
    aiAssistClient.get<TranscriptResponse>(`/sessions/${sessionId}/transcript`, signal),

  /* ── Extraction, diagnoses, tests (backend §3, §4, §5) ───────────────── */

  /** Runs an extraction pass now. Degrades to a 200 with `ai.ok === false`. */
  analyse: (sessionId: string) =>
    aiAssistClient.post<AnalyseResponse>(`/sessions/${sessionId}/analyse`),

  addClinicalItem: (sessionId: string, bucket: ClinicalBucket, payload: Record<string, string>) =>
    aiAssistClient.post<SessionResponse>(`/sessions/${sessionId}/clinical/${bucket}`, payload),

  actOnClinicalItem: (
    sessionId: string,
    bucket: ClinicalBucket,
    itemId: string,
    action: ClinicalAction,
    patch?: Record<string, string>,
  ) =>
    aiAssistClient.patch<SessionResponse>(`/sessions/${sessionId}/clinical/${bucket}/${itemId}`, {
      action,
      ...(patch ? { patch } : {}),
    }),

  /* ── Clinical context (backend §6, §7, §8) ───────────────────────────── */

  getContext: (sessionId: string, signal?: AbortSignal) =>
    aiAssistClient.get<{ context: CopilotContext }>(`/sessions/${sessionId}/context`, signal),

  /* ── End-of-call processing (backend §10, §16) ───────────────────────── */

  /** Returns 202 immediately — it cannot delay the call ending. */
  finalize: (sessionId: string, reason = "call_ended") =>
    aiAssistClient.post<{ accepted: boolean; alreadyRunning: boolean }>(
      `/sessions/${sessionId}/finalize`,
      { reason },
    ),

  retry: (sessionId: string) =>
    aiAssistClient.post<{ accepted: boolean; alreadyRunning: boolean }>(
      `/sessions/${sessionId}/retry`,
    ),

  /* ── Prescription (backend §9, §11, §12) ─────────────────────────────── */

  getPrescription: (sessionId: string, signal?: AbortSignal) =>
    aiAssistClient.get<{ prescription: Prescription }>(
      `/sessions/${sessionId}/prescription`,
      signal,
    ),

  /** Starts a prescription without AI. Returns the existing draft if there is one. */
  startPrescription: (sessionId: string) =>
    aiAssistClient.post<{ prescription: Prescription }>(`/sessions/${sessionId}/prescription`),

  markUnderReview: (prescriptionId: string) =>
    aiAssistClient.post<{ prescription: Prescription }>(`/prescriptions/${prescriptionId}/review`),

  updatePrescription: (prescriptionId: string, patch: Record<string, unknown>) =>
    aiAssistClient.patch<{ prescription: Prescription }>(`/prescriptions/${prescriptionId}`, patch),

  approve: (prescriptionId: string, input: { signature: string; doctorName?: string }) =>
    aiAssistClient.post<{ prescription: Prescription }>(
      `/prescriptions/${prescriptionId}/approve`,
      input,
    ),

  issue: (prescriptionId: string) =>
    aiAssistClient.post<IssueResponse>(`/prescriptions/${prescriptionId}/issue`),

  /* ── Summary and audit (backend §14, §15) ────────────────────────────── */

  getSummary: (sessionId: string, signal?: AbortSignal) =>
    aiAssistClient.get<{ summary: ConsultationSummary }>(`/sessions/${sessionId}/summary`, signal),

  regenerateSummary: (sessionId: string) =>
    aiAssistClient.post<{ summary: ConsultationSummary }>(`/sessions/${sessionId}/summary`),

  getAudit: (sessionId: string, signal?: AbortSignal) =>
    aiAssistClient.get<{ events: AuditEvent[] }>(`/sessions/${sessionId}/audit`, signal),
};
